import { FEATURE_FLAG_PREFIX, DEFAULT_EXPIRY_MS, FEATURE_FLAG_MODULES } from './constants';
const storage = window.sessionStorage;

/**
 * The function `getStorageKey` returns a storage key by combining a prefix with a flag name.
 * @param flagName - The `flagName` parameter is a string representing the name of a feature flag.
 * @returns a string that concatenates the value of the `FEATURE_FLAG_PREFIX` variable with the
 * `flagName` parameter.
 */
function getStorageKey(flagName) {
  return `${FEATURE_FLAG_PREFIX}${flagName}`;
}

/**
 * The function `setFeatureFlag` stores a feature flag value with an expiry time in local storage.
 * @param flagName - The `flagName` parameter is a string representing the name of the feature flag
 * that you want to set.
 * @param value - The `value` parameter in the `setFeatureFlag` function represents the value
 * associated with the feature flag. This value could be a boolean, string, number, or any other data
 * type that indicates whether the feature flag is enabled or disabled.
 * @param [expiryMs] - The `expiryMs` parameter in the `setFeatureFlag` function represents the
 * duration in milliseconds for which the feature flag will be active before it expires. If no value is
 * provided for `expiryMs`, it defaults to `DEFAULT_EXPIRY_MS`.
 */
function setFeatureFlag(flagName, value, expiryMs = DEFAULT_EXPIRY_MS) {
  const expiry = Date.now() + expiryMs;
  const data = { value, expiry };
  storage.setItem(getStorageKey(flagName), JSON.stringify(data));
}

/**
 * The function `hardReloadWithCleanURL` removes specific query parameters from the hash portion of the
 * current URL and reloads the page if any matching parameters are found.
 * @param url - The `url` parameter in the `hardReloadWithCleanURL` function is the URL that you want
 * to use for the hard reload with a clean URL.
 */
function hardReloadWithCleanURL(url) {
  const currentUrl = new URL(window.location.href);
  const hash = currentUrl.hash;
  if (hash?.includes('?')) {
    const [hashPath, hashQuery] = hash.split('?');
    const urlParams = new URLSearchParams(hashQuery);
    const keysToRemove = [];
    for (const [key] of urlParams.entries()) {
      if (key.startsWith(FEATURE_FLAG_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => urlParams.delete(key));
    const newHashQuery = urlParams.toString();
    const newHash = newHashQuery ? `${hashPath}?${newHashQuery}` : hashPath;
    currentUrl.hash = newHash;
    const newUrl = currentUrl.toString();
    if (keysToRemove.length > 0) {
      window.location.assign(newUrl);
      window.location.reload();
    }
  }
}

/**
 * The function `getFeatureFlag` retrieves a feature flag value from storage, considering expiration
 * time.
 * @param flagName - The `flagName` parameter is used to specify the name of the feature flag whose
 * value you want to retrieve from storage.
 * @returns The function `getFeatureFlag` returns the value of the feature flag corresponding to the
 * `flagName` if it is found in the storage and has not expired. If the flag is found and has not
 * expired, the function returns the value of the flag. If the flag is not found or has expired, the
 * function returns `null`.
 */
function getFeatureFlag(flagName) {
  const item = storage.getItem(getStorageKey(flagName));
  if (!item) return null;

  try {
    const data = JSON.parse(item);
    if (Date.now() > data.expiry) {
      storage.removeItem(getStorageKey(flagName));
      return null;
    }
    return data.value;
  } catch {
    return null;
  }
}

/**
 * The function `removeFeatureFlag` removes a feature flag by deleting its corresponding storage key.
 * @param flagName - The `flagName` parameter is a string that represents the name of the feature flag
 * that you want to remove from storage.
 */
function removeFeatureFlag(flagName) {
  storage.removeItem(getStorageKey(flagName));
}

/**
 * The function `parseFlagsFromURL` extracts feature flags from a URL query string based on a specific
 * prefix and returns them as key-value pairs.
 * @param url - It seems like the `url` parameter is expected to be an object containing a `query`
 * property. The `parseFlagsFromURL` function then extracts feature flags from the query parameters of
 * the URL.
 * @returns The `parseFlagsFromURL` function returns an object containing feature flags parsed from the
 * URL query parameters. The function extracts query parameters that start with a specific prefix
 * (defined as `FEATURE_FLAG_PREFIX`), removes the prefix from the key, and stores the flag name as a
 * key in the result object with a boolean value based on whether the corresponding value in the URL is
 * 'true'.
 */
function parseFlagsFromURL(url) {
  const urlParams = new URLSearchParams(url.query);
  const urlParamEntries = urlParams.entries();
  const result = {};
  for (const [key, value] of urlParamEntries) {
    if (key.startsWith(FEATURE_FLAG_PREFIX)) {
      const flagName = key.slice(3);
      result[flagName] = value === 'true';
    }
  }
  return result;
}

/**
 * The function `applyURLFeatureFlags` parses feature flags from a URL, sets them, and triggers a hard
 * reload if any flags were updated.
 * @param url - The `applyURLFeatureFlags` function takes a URL as a parameter. This URL is used to
 * parse feature flags and update them accordingly. If any flags are updated, a hard reload is
 * triggered with the same URL.
 */
function applyURLFeatureFlags(url) {
  const flags = parseFlagsFromURL(url);
  let flagsUpdated = false;
  for (const [flagName, value] of Object.entries(flags)) {
    setFeatureFlag(flagName, value);
    flagsUpdated = true;
  }
  // Hard reload if flags were updated
  if (flagsUpdated) {
    hardReloadWithCleanURL(url);
  }
}

/**
 * The function `isFeatureEnabled` checks if a specific feature is enabled based on the module name and
 * feature flag settings.
 * @param moduleName - The `moduleName` parameter is used to specify the name of the module for which
 * you want to check if the feature is enabled.
 * @returns The function `isFeatureEnabled` returns the value of the feature flag for the specified
 * `moduleName`. If the `moduleName` is found in the `FEATURE_FLAG_MODULES`, it checks the flag value
 * and returns either `true` or `false` based on the flag value. If the `moduleName` is not found in
 * the `FEATURE_FLAG_MODULES`, it returns `true` by default.
 */
function isFeatureEnabled(moduleName) {
  const featureFlagModules = FEATURE_FLAG_MODULES;
  const shouldCheck = featureFlagModules.hasOwnProperty(moduleName);
  const moduleDefault = featureFlagModules[moduleName];
  let flagValue = true;
  if (shouldCheck) {
    if (moduleDefault === null) {
      flagValue = Boolean(getFeatureFlag(moduleName));
    } else {
      flagValue = moduleDefault;
    }
  }
  return flagValue;
}

export { getFeatureFlag, setFeatureFlag, removeFeatureFlag, applyURLFeatureFlags, isFeatureEnabled };
